import PropertySearch from "../../components/property-search";
import { useState, useEffect, useCallback } from "react";
import { PropertyCard } from "../../components/property-card";
import NearbyPropertiesMap from "../../components/NearbyPropertiesMap";
import { Card, CardContent } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import {
  Search as SearchIcon,
  Home,
  Grid3x3,
  List,
  Map as MapIcon,
  ArrowUpDown,
  MapPin,
  DollarSign,
  Building2,
  HomeIcon,
  TrendingUp,
  X,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../contexts/AuthContext";
import { resolveMapSearchTarget } from "../../utils/mapSearchArea";

// Re-export PropertyCard for backward compatibility
export { PropertyCard };

const Search = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState("relevant");
  const [totalCount, setTotalCount] = useState(0);
  const [mapProperties, setMapProperties] = useState([]);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'map'
  const [isLgUp, setIsLgUp] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : false
  );
  const [searchAreaTarget, setSearchAreaTarget] = useState(null);
  const [resolvingArea, setResolvingArea] = useState(false);
  const [areaError, setAreaError] = useState(null);

  useEffect(() => {
    if (properties.length === 0) setMobileView("list");
  }, [properties.length]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsLgUp(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const mapPanelHeight =
    !isLgUp && mobileView === "map" ? "70vh" : "calc(100vh - 140px)";

  // Determine user role for personalization
  const userRole = user?.user_role;
  const isHomeowner = userRole === "homeowner";
  const isInvestor = userRole === "investor";

  const handlePropertySearch = (next) => {
    setProperties(next);
    setAreaError(null);
  };

  const clearSearchAreaTarget = useCallback(() => {
    setSearchAreaTarget(null);
  }, []);

  const handleSearchThisArea = useCallback(
    async (coords) => {
      setResolvingArea(true);
      setAreaError(null);
      try {
        const target = await resolveMapSearchTarget(coords, mapProperties);
        setSearchAreaTarget(target);
      } catch {
        setAreaError(
          "Couldn't find properties for this area. Try moving the map closer to a town or ZIP."
        );
      } finally {
        setResolvingArea(false);
      }
    },
    [mapProperties]
  );

  const handleTotalCountChange = (count) => {
    setTotalCount(count);
  };

  // Sort properties based on selected sort option
  const sortedProperties = [...properties].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return (a.estimatedValue || 0) - (b.estimatedValue || 0);
      case "price-high":
        return (b.estimatedValue || 0) - (a.estimatedValue || 0);
      case "sqft-low":
        return (a.squareFeet || 0) - (b.squareFeet || 0);
      case "sqft-high":
        return (b.squareFeet || 0) - (a.squareFeet || 0);
      case "bedrooms":
        return (b.bedrooms || 0) - (a.bedrooms || 0);
      default:
        return 0; // relevant (default order)
    }
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="border-b">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-10 max-w-[1400px]">
          <div className="text-center mb-4 sm:mb-6">
            {/* Personalized Header */}
            {isHomeowner ? (
              <>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <HomeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-primary via-orange-500 to-orange-600 bg-clip-text text-transparent">
                    Find Your Property
                  </h1>
                </div>
                <p className="text-muted-foreground text-xs sm:text-sm md:text-base px-4 mb-3">
                  Enter your property address to get an instant valuation and see how much you can earn
                </p>
                <div className="flex flex-wrap gap-2 justify-center items-center text-xs sm:text-sm">
                  <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                    💰 Get up to 2 years rent upfront
                  </Badge>
                  <Badge variant="secondary" className="bg-orange-500/10 text-orange-700 border-orange-500/20">
                    🏠 Free property valuation
                  </Badge>
                  <Badge variant="secondary" className="bg-green-500/10 text-green-700 border-green-500/20">
                    ✅ Upto 15% guaranteed return
                  </Badge>
                </div>
              </>
            ) : isInvestor ? (
              <>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-primary via-orange-500 to-orange-600 bg-clip-text text-transparent">
                    Investment Opportunities
                  </h1>
                </div>
                <p className="text-muted-foreground text-xs sm:text-sm md:text-base px-4 mb-3">
                  Explore properties with detailed ROI analysis and investment potential
                </p>
                <div className="flex flex-wrap gap-2 justify-center items-center text-xs sm:text-sm">
                  <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                    📈 14-18% annual returns
                  </Badge>
                  <Badge variant="secondary" className="bg-purple-500/10 text-purple-700 border-purple-500/20">
                    🪙 Token-based investing
                  </Badge>
                  <Badge variant="secondary" className="bg-blue-500/10 text-blue-700 border-blue-500/20">
                    🚪 Early exit options
                  </Badge>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 bg-gradient-to-r from-primary via-orange-500 to-orange-600 bg-clip-text text-transparent px-2">
                  Find Your Next Property
                </h1>
                <p className="text-muted-foreground text-xs sm:text-sm md:text-base px-4">
                  Search from thousands of properties across the United States
                </p>
              </>
            )}
          </div>

          {/* Search Component */}
          <PropertySearch
            onChange={handlePropertySearch}
            setIsLoadingProperties={setIsLoading}
            onTotalCountChange={handleTotalCountChange}
            onMapPropertiesChange={setMapProperties}
            setIsLoadingMapProperties={setIsLoadingMap}
            externalTarget={searchAreaTarget}
            onExternalTargetApplied={clearSearchAreaTarget}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px]">
        {/* Results Section */}
        {isLoading ? (
          <div>
            {/* Header Skeleton */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-5">
              <Skeleton className="h-8 w-full sm:w-48" />
              <Skeleton className="h-10 w-full sm:w-32" />
            </div>

            {/* Cards Skeleton */}
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {[...Array(8)].map((_, index) => (
                <Card key={index} className="overflow-hidden">
                  <Skeleton className="h-[160px] sm:h-[180px] w-full" />
                  <div className="p-2 sm:p-3 space-y-2">
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ) : properties.length > 0 ? (
          <div>
            {/* Mobile: List / Map toggle */}
            <div className="flex gap-2 mb-4 lg:hidden">
              <Button
                type="button"
                variant={mobileView === "list" ? "default" : "outline"}
                size="sm"
                className="flex-1 gap-2"
                onClick={() => setMobileView("list")}
              >
                <List className="h-4 w-4" />
                List
              </Button>
              <Button
                type="button"
                variant={mobileView === "map" ? "default" : "outline"}
                size="sm"
                className="flex-1 gap-2"
                onClick={() => setMobileView("map")}
              >
                <MapIcon className="h-4 w-4" />
                Map
              </Button>
            </div>

            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)] lg:gap-6 lg:items-start">
              {/* List column */}
              <div
                className={
                  mobileView === "map"
                    ? "hidden lg:block lg:min-h-[calc(100vh-220px)]"
                    : "lg:min-h-[calc(100vh-220px)]"
                }
              >
                {/* Results Header with Controls */}
                <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-5 bg-card p-3 sm:p-4 md:p-5 rounded-lg border shadow-sm">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-1" />
                      <div>
                        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground">
                          {totalCount.toLocaleString()} Properties Found
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                          Showing {properties.length} properties on this page
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="flex-1 sm:flex-none sm:w-[180px] md:w-[200px] h-9 sm:h-10">
                          <ArrowUpDown className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="relevant">Most Relevant</SelectItem>
                          <SelectItem value="price-low">
                            Price: Low to High
                          </SelectItem>
                          <SelectItem value="price-high">
                            Price: High to Low
                          </SelectItem>
                          <SelectItem value="sqft-low">
                            Size: Small to Large
                          </SelectItem>
                          <SelectItem value="sqft-high">
                            Size: Large to Small
                          </SelectItem>
                          <SelectItem value="bedrooms">Most Bedrooms</SelectItem>
                        </SelectContent>
                      </Select>

                      <div className="hidden sm:flex items-center gap-1 bg-muted p-1 rounded-lg">
                        <Button
                          variant={viewMode === "grid" ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setViewMode("grid")}
                          className="h-8 w-8 p-0"
                        >
                          <Grid3x3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={viewMode === "list" ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setViewMode("list")}
                          className="h-8 w-8 p-0"
                        >
                          <List className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={
                    viewMode === "grid"
                      ? "grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 md:gap-5"
                      : "flex flex-col gap-3 sm:gap-4"
                  }
                >
                  {sortedProperties.map((property) => (
                    <PropertyCard
                      property={property}
                      key={property.id}
                      redirectOnClick={true}
                      showPrices={true}
                      imageHeight={viewMode === "list" ? 200 : 180}
                    />
                  ))}
                </div>
              </div>

              {/* Map column (sticky on desktop) */}
              <div
                className={
                  mobileView === "list"
                    ? "hidden lg:block mt-0"
                    : "mt-4 lg:mt-0"
                }
              >
                <div className="lg:sticky lg:top-4 space-y-2">
                  {areaError && (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive flex justify-between gap-2 items-start"
                    >
                      <span>{areaError}</span>
                      <button
                        type="button"
                        onClick={() => setAreaError(null)}
                        className="shrink-0 rounded p-0.5 hover:bg-destructive/15"
                        aria-label="Dismiss"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  {isLoadingMap && mapProperties.length === 0 ? (
                    <Card className="overflow-hidden shadow-md border">
                      <CardContent className="p-4 space-y-3">
                        <Skeleton className="h-6 w-40" />
                        <Skeleton
                          className="w-full rounded-md"
                          style={{ height: mapPanelHeight }}
                        />
                      </CardContent>
                    </Card>
                  ) : !isLoadingMap && mapProperties.length === 0 ? (
                    <Card className="shadow-md border">
                      <CardContent
                        className="p-6 text-center text-muted-foreground text-sm flex items-center justify-center"
                        style={{ minHeight: mapPanelHeight }}
                      >
                        No properties with coordinates to show on the map for
                        this search. Try a broader area or different filters.
                      </CardContent>
                    </Card>
                  ) : (
                    <NearbyPropertiesMap
                      properties={mapProperties}
                      height={mapPanelHeight}
                      onSearchArea={handleSearchThisArea}
                      searchAreaLoading={resolvingArea}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Card className="mt-4 sm:mt-6 md:mt-8">
            <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12 md:py-16 px-4">
              <div className="p-4 sm:p-6 bg-muted rounded-full mb-3 sm:mb-4">
                {isHomeowner ? (
                  <HomeIcon className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-primary" />
                ) : isInvestor ? (
                  <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-primary" />
                ) : (
                  <SearchIcon className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-muted-foreground" />
                )}
              </div>
              
              {/* Personalized Empty State */}
              {isHomeowner ? (
                <>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2 text-center px-2">
                    Find Your Property's Value
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center max-w-md mb-4 sm:mb-6 px-4">
                    Enter your property address above to get an instant valuation, 
                    see renovation potential, and learn how much rent upfront you can receive
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center px-2 mb-4">
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 bg-primary/5"
                    >
                      <Home className="h-3 w-3 mr-1" />
                      <span>Your Property</span>
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 bg-orange-500/5"
                    >
                      <DollarSign className="h-3 w-3 mr-1" />
                      <span>Instant Valuation</span>
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 bg-green-500/5"
                    >
                      <span>💰 Rent Upfront</span>
                    </Badge>
                  </div>
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 max-w-lg text-center">
                    <p className="text-xs sm:text-sm text-primary font-medium">
                      💡 <strong>Pro Tip:</strong> Enter your exact street address for the most accurate valuation and personalized offer
                    </p>
                  </div>
                </>
              ) : isInvestor ? (
                <>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2 text-center px-2">
                    Discover Investment Opportunities
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center max-w-md mb-4 sm:mb-6 px-4">
                    Search by location to find properties with high ROI potential, 
                    detailed financial analysis, and token-based investment options
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center px-2 mb-4">
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 bg-emerald-500/5"
                    >
                      <TrendingUp className="h-3 w-3 mr-1" />
                      <span>High ROI</span>
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 bg-purple-500/5"
                    >
                      <span>🪙 Token Investing</span>
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 bg-blue-500/5"
                    >
                      <Building2 className="h-3 w-3 mr-1" />
                      <span>Multi-Family</span>
                    </Badge>
                  </div>
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 max-w-lg text-center">
                    <p className="text-xs sm:text-sm text-primary font-medium">
                      📊 <strong>Investment Focus:</strong> Search in growing markets for properties with 14-18% annual returns
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2 text-center px-2">
                    Start Your Property Search
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center max-w-md mb-4 sm:mb-6 px-4">
                    Enter an address, city, or ZIP code above to discover amazing
                    properties in your desired location
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center px-2">
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4"
                    >
                      <Home className="h-3 w-3 mr-1" />
                      <span className="hidden xs:inline">Single Family Homes</span>
                      <span className="xs:hidden">Homes</span>
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4"
                    >
                      <Building2 className="h-3 w-3 mr-1" />
                      <span className="hidden xs:inline">
                        Multi-Family Properties
                      </span>
                      <span className="xs:hidden">Multi-Family</span>
                    </Badge>
                    <Badge
                      variant="outline"
                      className="text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4"
                    >
                      <DollarSign className="h-3 w-3 mr-1" />
                      <span className="hidden xs:inline">
                        Investment Opportunities
                      </span>
                      <span className="xs:hidden">Investments</span>
                    </Badge>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Search;
